'use client'

import { Price } from '@/components/Price'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import { useCart } from '@payloadcms/plugin-ecommerce/client/react'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useEffect, useMemo, useState } from 'react'

import { DeleteItemButton } from './DeleteItemButton'
import { EditItemQuantityButton } from './EditItemQuantityButton'
import { OpenCartButton } from './OpenCart'
import { Button } from '@/components/ui/button'
import { Logo } from '@/components/brand/Logo'
import { cn } from '@/utilities/cn'
import { placeholderTone } from '@/utilities/placeholderTone'
import { productImage } from '@/utilities/productImage'
import type { CartItem } from '@/components/Cart'
import type { Variant } from '@/payload-types'

/** «M, Чёрный» — подпись выбранного варианта. */
const variantLabel = (variant: Variant | null): string =>
  (variant?.options ?? [])
    .map((option) => (typeof option === 'object' ? option.label : null))
    .filter(Boolean)
    .join(', ')

function CartLine({ item }: { item: CartItem }) {
  const product = typeof item.product === 'object' ? item.product : null
  const variant = typeof item.variant === 'object' ? item.variant : null

  if (!product?.slug) return null

  const image = productImage(product, variant)
  const unitPrice = variant ? variant.priceInRUB : product.priceInRUB
  const quantity = item.quantity ?? 0
  const stock = variant?.inventory ?? product.inventory
  const atLimit = typeof stock === 'number' && quantity >= stock
  const label = variantLabel(variant)

  return (
    <li className="flex gap-4 py-5">
      <Link
        aria-hidden
        className="shrink-0"
        href={`/products/${product.slug}`}
        tabIndex={-1}
      >
        <div
          className={cn(
            'relative h-24 w-20 overflow-hidden rounded-xl',
            image?.url ? 'bg-foam' : placeholderTone(product.id),
          )}
        >
          {image?.url ? (
            <Image
              alt={image.alt || product.title || ''}
              className="h-full w-full object-cover"
              height={160}
              src={image.url}
              width={128}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Logo variant="sun" title="" aria-hidden className="h-1/2 w-auto opacity-30" />
            </div>
          )}
        </div>
      </Link>

      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <Link className="font-display text-sm leading-tight text-ink transition-colors hover:text-punch" href={`/products/${product.slug}`}>
              {product.title}
            </Link>
            {label ? <p className="mt-0.5 text-xs text-ink/60">{label}</p> : null}
          </div>
          <DeleteItemButton item={item} />
        </div>

        <div className="mt-auto flex items-end justify-between gap-3">
          <div className="flex h-9 items-center rounded-full border-2 border-ink/15 bg-foam">
            <EditItemQuantityButton item={item} type="minus" />
            <span className="w-7 text-center text-sm tabular-nums text-ink">{quantity}</span>
            <EditItemQuantityButton item={item} type="plus" />
          </div>

          {typeof unitPrice === 'number' && (
            <div className="text-right">
              <Price as="span" amount={unitPrice * quantity} className="font-display text-sm text-ink" />
              {quantity > 1 && (
                <p className="text-xs text-ink/50">
                  <Price as="span" amount={unitPrice} /> за штуку
                </p>
              )}
            </div>
          )}
        </div>

        {atLimit && <p className="text-xs text-punch">Это всё, что осталось на складе</p>}
      </div>
    </li>
  )
}

export function CartModal({ quiet }: { quiet?: boolean } = {}) {
  const { cart } = useCart()
  const [isOpen, setIsOpen] = useState(false)

  const pathname = usePathname()

  useEffect(() => {
    // закрываем корзину при переходе на другую страницу
    setIsOpen(false)
  }, [pathname])

  const items = useMemo(() => cart?.items ?? [], [cart])

  const totalQuantity = useMemo(() => {
    if (!items.length) return undefined
    return items.reduce((quantity, item) => (item.quantity || 0) + quantity, 0)
  }, [items])

  return (
    <Sheet onOpenChange={setIsOpen} open={isOpen}>
      <SheetTrigger asChild>
        <OpenCartButton quantity={totalQuantity} quiet={quiet} />
      </SheetTrigger>

      <SheetContent className="w-full gap-0 border-l-2 border-ink bg-sand p-0 sm:max-w-md">
        <SheetHeader className="gap-1 bg-sun px-5 py-5">
          <SheetTitle className="flex items-center gap-2 font-display text-lg uppercase tracking-wide text-ink">
            Корзина
            {totalQuantity ? (
              <span className="inline-flex h-6 min-w-6 items-center justify-center rounded-full bg-ink px-2 text-xs font-bold text-sun">
                {totalQuantity}
              </span>
            ) : null}
          </SheetTitle>
          <SheetDescription className={cn('text-sm text-ink/70', !items.length && 'sr-only')}>
            {items.length ? 'Проверьте размеры и количество.' : 'Пока пусто.'}
          </SheetDescription>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-5 px-8 text-center">
            <div className="flex h-24 w-24 items-center justify-center rounded-full bg-sun">
              <Logo variant="sun" title="" aria-hidden className="h-12 w-auto text-ink/40" />
            </div>
            <div>
              <p className="font-display text-xl text-ink">Здесь пока пусто</p>
              <p className="mt-1 text-sm text-ink/60">Форма, футболки и мешки клуба — в каталоге.</p>
            </div>
            <SheetClose asChild>
              <Button asChild>
                <Link href="/shop">Выбрать мерч</Link>
              </Button>
            </SheetClose>
          </div>
        ) : (
          <>
            <ul className="flex-1 divide-y divide-ink/10 overflow-y-auto px-5">
              {items.map((item, i) => (
                <CartLine item={item} key={item.id ?? i} />
              ))}
            </ul>

            <SheetFooter className="gap-3 bg-foam px-5 py-5">
              {typeof cart?.subtotal === 'number' && (
                <div className="flex items-baseline justify-between">
                  <span className="font-display text-xs uppercase tracking-wide text-ink/60">Итого</span>
                  <Price as="span" amount={cart.subtotal} className="font-display text-xl text-ink" />
                </div>
              )}

              <Button asChild size="lg" className="w-full">
                <Link href="/checkout">Оформить заказ</Link>
              </Button>

              <SheetClose asChild>
                <button className="text-center text-xs uppercase tracking-wide text-ink/50 transition-colors hover:text-punch" type="button">
                  Продолжить покупки
                </button>
              </SheetClose>
            </SheetFooter>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
